import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

/**
 * Client-side safety net against the 413 PayloadTooLargeError that fires when a
 * `campaignConfig` (serialized into the body of `PATCH list/:id` and
 * `PATCH list_person/:id`) exceeds the API body-parser limit.
 *
 * The durable fix is forbidding base64 images in the campaign editors
 * (see {@link file://./tinymceEditor.constants.ts}); this guard is the last line
 * of defence so an oversized config is rejected in the browser with a clear
 * message instead of firing a request the server can only answer with an opaque
 * 413. The check is enforced centrally in the API `baseQueryWithValidation`, so
 * it covers every endpoint that ships a `campaignConfig`.
 */

// The API accepts up to 25 MB (see apps/api/src/main.ts). Stay well under that to
// leave headroom for the rest of the request body, header overhead and the
// byte-vs-char difference of UTF-8 encoding.
export const CAMPAIGN_CONFIG_MAX_BYTES = 20 * 1024 * 1024;

/** Stable marker so the global error middleware can recognise this rejection. */
export const CAMPAIGN_CONFIG_TOO_LARGE_ERROR = "campaign-config-too-large";

export const CAMPAIGN_CONFIG_TOO_LARGE_MESSAGE =
	"This campaign is too large to save. Remove large pasted content (such as images) or split it into multiple campaigns.";

/** UTF-8 byte length of a string (falls back to char length where unavailable). */
export const getByteSize = (value: string): number =>
	typeof TextEncoder !== "undefined" ? new TextEncoder().encode(value).length : value.length;

/**
 * True when `campaignConfig` is a string larger than the allowed byte budget.
 * Non-string values (the field is absent on most requests) are always allowed.
 */
export const isCampaignConfigTooLarge = (campaignConfig: unknown): boolean =>
	typeof campaignConfig === "string" && getByteSize(campaignConfig) > CAMPAIGN_CONFIG_MAX_BYTES;

/** The error returned by the base query when the guard trips. */
export const campaignConfigTooLargeError: FetchBaseQueryError = {
	status: "CUSTOM_ERROR",
	error: CAMPAIGN_CONFIG_TOO_LARGE_ERROR,
	data: { message: CAMPAIGN_CONFIG_TOO_LARGE_MESSAGE },
};
